import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from 'react-redux';
import "../Custom.css"; // Import separate CSS

const WhatsappDrawer = ({ isOpen, onClose, onSave, initialData, authToken, templates = [], loading = false, nodeId }) => {

  // Helper function to get template field values
  const getTemplateValue = (template, fieldName) => {
    return template[fieldName] || template[fieldName.toLowerCase()] || template[fieldName.toUpperCase()] || '';
  };

  const notificationDetails = useSelector(
    (state) => state.journey.notification_details || []
  );

  const detailsMap = notificationDetails?.data || {};

  // get the current node's detail
  const nodeKey = String(nodeId);
  const nodeDetail = detailsMap[nodeKey] || null;
  const wp_id = nodeDetail?.wp_id;
  const templateimage = nodeDetail?.image;
  
  // Helper to check if URL is valid (not an error message)
  const isValidFileUrl = (url) => {
    if (!url) return false;
    // Check for invalid file format messages
    if (url.includes('!invalid') || url.includes('invalid file') || url.includes('error')) {
      return false;
    }
    // Check if it's a data URL (base64)
    if (url.startsWith('data:')) return true;
    // Check if it's a valid HTTP/HTTPS URL
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    // Check if it's a relative path (starts with /)
    if (url.startsWith('/')) return true;
    return false;
  };

  // Helper to check if URL looks like a PDF
  const isPdfUrl = (url) => {
    if (!url) return false;
    if (url.startsWith('data:application/pdf')) return true;
    const lowerUrl = url.toLowerCase();
    // Check for .pdf extension or application/pdf in URL
    // Also check if file_type is pdf (more reliable than URL pattern)
    return lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf');
  };


  useEffect(() => {
   
   
  }, [templates, loading, authToken]);
  
  const getTemplateId = useCallback((template) => {
    // Convert to string for comparison since select values are strings
    const id = getTemplateValue(template, 'content_id') || 
               getTemplateValue(template, 'id') || 
               getTemplateValue(template, 'template_id') || 
               template.id || '';
    return String(id);
  }, []);

  const getTemplateTitle = (template) => {
    return getTemplateValue(template, 'content_title') || 
           getTemplateValue(template, 'title') || 
           getTemplateValue(template, 'name') || 
           getTemplateValue(template, 'template_title') || 
           getTemplateValue(template, 'campaign_name') ||
           getTemplateValue(template, 'campaign_title') ||
           getTemplateValue(template, 'subject') ||
           template.title || template.name || template.campaign_name || template.subject || '';
  };

  const [campaignName, setCampaignName] = useState(
    initialData?.whatsappConfig?.campaignName || ""
  );

  const [template, setTemplate] = useState(
    initialData?.whatsappConfig?.template || ""
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState("");
  const [showFileSection, setShowFileSection] = useState(false);
  const [fileType, setFileType] = useState('image'); // 'image', 'pdf', 'video', or null
  const [description, setDescription] = useState("");
  const [fileRemoved, setFileRemoved] = useState(false);

  // Compute preview URL: use templateimage (saved URL) or construct data URL from fileBase64 (new file)
  const getPreviewUrl = () => {
    if (selectedFile && fileBase64) {
      // New file selected - construct data URL from base64
      const mimeType = selectedFile.type || (fileType === 'image' ? 'image/jpeg' : fileType === 'pdf' ? 'application/pdf' : 'video/mp4');
      return `data:${mimeType};base64,${fileBase64}`;
    }
    // Use saved URL from templateimage
    if (templateimage) {
      const withCacheBuster = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}` : '');
      return withCacheBuster(templateimage);
    }
    return null;
  };
  
  const previewUrl = getPreviewUrl();

  // Helper function to extract filename from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    // If we have selectedFile, use its name
    if (selectedFile) {
      return selectedFile.name;
    }
    // Extract filename from URL path
    try {
      // Remove query parameters and hash
      const urlWithoutParams = url.split('?')[0].split('#')[0];
      // Extract filename from path
      const fileName = urlWithoutParams.split('/').pop();
      // If filename exists and is not empty, return it
      if (fileName && fileName.length > 0 && fileName !== urlWithoutParams) {
        return fileName;
      }
      // Fallback: generate a name based on file type
      const extension = fileType === 'image' ? 'jpg' : fileType === 'pdf' ? 'pdf' : 'mp4';
      return `file.${extension}`;
    } catch (e) {
      // Fallback: generate a name based on file type
      const extension = fileType === 'image' ? 'jpg' : fileType === 'pdf' ? 'pdf' : 'mp4';
      return `file.${extension}`;
    }
  };

  // Get current file name for display in edit mode
  const currentFileName = previewUrl && !selectedFile ? getFileNameFromUrl(previewUrl) : (selectedFile ? selectedFile.name : null);

  // Check if selected template requires file (image, pdf, or video)
  const checkTemplateFileRequirement = useCallback((templateId) => {
    if (!templateId || !templates || templates.length === 0) {
      setShowFileSection(false);
      setFileType(null);
      // Clear any previous file if template selection is empty
      setSelectedFile(null);
      setFileBase64("");
      setFileRemoved(false);
      return;
    }

    const selectedTemplate = templates.find(t => getTemplateId(t) === String(templateId));
    if (selectedTemplate) {
      const templateFileType = selectedTemplate.file_type || null;
      const requiresFile = templateFileType === 'image' || templateFileType === 'pdf' || templateFileType === 'video';
      
      // If the required file type changed (e.g., video -> image), clear any stale preview/file
      setFileType(prev => {
        const prevType = prev;
        if (templateFileType && prevType && templateFileType !== prevType) {
          setSelectedFile(null);
          setFileBase64("");
          setFileRemoved(false);
        }
        return templateFileType;
      });
      setShowFileSection(requiresFile);
      
      if (!requiresFile) {
        // If new template does not require file, clear any existing preview/upload
        setSelectedFile(null);
        setFileBase64("");
        setFileRemoved(false);
      }
    } else {
      setShowFileSection(false);
      setFileType(null);
      setSelectedFile(null);
      setFileBase64("");
      setFileRemoved(false);
    }
  }, [templates, getTemplateId]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type based on file_type requirement
      if (fileType === 'image' && !file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      if (fileType === 'pdf' && file.type !== 'application/pdf') {
        alert('Please select a valid PDF file');
        return;
      }
      if (fileType === 'video' && !file.type.startsWith('video/')) {
        alert('Please select a valid video file');
        return;
      }
      
      // Size validation (5MB for images, larger for videos/PDFs)
      const maxSize = fileType === 'video' ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for others
      if (file.size > maxSize) {
        const sizeLimit = fileType === 'video' ? '50MB' : '5MB';
        alert(`File size should be less than ${sizeLimit}`);
        return;
      }
      
      setSelectedFile(file);
      setFileRemoved(false);
      
      // For all file types (image, pdf, video), convert to base64 for consistent payload format
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result || "";
        // Extract pure base64 (strip 'data:*/*;base64,' prefix)
        const commaIdx = String(dataUrl).indexOf(",");
        const base64 = commaIdx !== -1 ? String(dataUrl).slice(commaIdx + 1) : String(dataUrl);
        setFileBase64(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFileBase64("");
    setFileRemoved(true);
  };

  useEffect(() => {
    if (initialData?.whatsappConfig) {
      setCampaignName(initialData.whatsappConfig.campaignName || "");
      setTemplate(initialData.whatsappConfig.template || "");
      setSelectedFile(initialData.whatsappConfig.selectedFile || initialData.whatsappConfig.selectedImage || null);
      // Preview will be handled by templateimage (saved URL from API) or fileBase64 (new file)
      setFileBase64("");
    }
  }, [initialData]);

  // Set file type from template when templateimage is available
  useEffect(() => {
    if (templateimage && !selectedFile && isOpen && template && templates && templates.length > 0) {
      const selectedTemplateObj = templates.find(t => getTemplateId(t) === String(template));
      if (selectedTemplateObj?.file_type) {
        setFileType(selectedTemplateObj.file_type);
        setShowFileSection(true);
      }
    }
  }, [templateimage, selectedFile, template, templates, getTemplateId, isOpen]);

  // Auto-select template from wp_id when available
  useEffect(() => {
    const wpId = initialData?.whatsappConfig?.wp_id;
    if (wpId && templates && templates.length > 0) {
      const desiredId = String(wpId);
      setTemplate(desiredId);
      checkTemplateFileRequirement(desiredId);
      const selectedTemplateObj = templates.find(t => getTemplateId(t) === desiredId);
      setDescription(selectedTemplateObj?.description || "");
    }
  }, [initialData, templates, checkTemplateFileRequirement, getTemplateId]);

  useEffect(() => {
    checkTemplateFileRequirement(template);

    if (templates && templates.length > 0 && template) {
      const selectedTemplateObj = templates.find(t => getTemplateId(t) === String(template));
      const desc = selectedTemplateObj?.description || "";
      setDescription(desc);
    } else {
      setDescription("");
    }
  }, [template, templates, checkTemplateFileRequirement, getTemplateId]);


  const handleSave = () => {
    
    // Validation for mandatory template selection
    if (!template || template.trim() === "") {
      alert('Please select a template');
      // Focus on template select
      const templateSelect = document.querySelector('select[className*="form-select"]');
      if (templateSelect) templateSelect.focus();
      return;
    }
    // File validation: require only if template needs file AND there is no existing preview and no new file
    if (showFileSection && !selectedFile && !previewUrl && !fileRemoved) {
      const fileTypeLabel = fileType === 'image' ? 'image' : fileType === 'pdf' ? 'PDF' : 'video';
      alert(`Please select a ${fileTypeLabel}`);
      return;
    }
    const selectedTemplateObj = templates?.find(t => getTemplateId(t) === String(template));
    const templateName = selectedTemplateObj ? (selectedTemplateObj.content_title || selectedTemplateObj.title || selectedTemplateObj.name || "") : "";
    // Get file_type from template
    const templateFileType = selectedTemplateObj?.file_type || null;
    
    // Prepare image data - send as object with base64 (full data URL) and file properties
    // Only send image data if a new file was selected (selectedFile exists)
    // If no new file is selected, don't send image (it's already saved on server)
    let imageData = null;
    
    if (showFileSection && selectedFile && fileBase64) {
      // Construct full data URL from base64 and file type
      const mimeType = selectedFile.type || (templateFileType === 'image' ? 'image/jpeg' : templateFileType === 'pdf' ? 'application/pdf' : 'video/mp4');
      const fullDataUrl = `data:${mimeType};base64,${fileBase64}`;
      
      imageData = {
        base64: fullDataUrl,
        file: {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        }
      };
    }

    onSave({
      wp_id: template,
      title: templateName,
      image: imageData, // Object with base64 and file, or null if no new file
      image_removed: fileRemoved ? 1 : 0,
      file_type: templateFileType || null,
      description,
    });
  };

  const handleClose = () => {
    setCampaignName("");
    setTemplate("");
    setSelectedFile(null);
    setFileBase64("");
    setShowFileSection(false);
    setFileType(null);
    setFileRemoved(false);
    onClose();
  };


  return (
    <>
      {isOpen && <div className="drawer-overlay" onClick={onClose} />}

      <div className={`drawer ${isOpen ? "open" : ""}`}>
        <div className="drawer-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <div className="nav nav-pills">
            Whatsapp Template
          </div>
          <button className="btn-close" onClick={handleClose}></button>
        </div>

        <div className="drawer-body p-4">
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            {/* Left Side - Form Fields */}
            <div style={{
              flex: '1',
              minWidth: '300px',
              maxWidth: '100%'
            }}>
              <label className="form-label">Select Template <span className="text-danger">*</span></label>
              <select
                className="form-select mb-3"
                value={template}
                onChange={(e) => {
                  setTemplate(e.target.value);
                  checkTemplateFileRequirement(e.target.value);
                }}
                disabled={loading}
                required
              >
                <option value="" disabled>
                  {loading ? "Loading templates..." : "Select..."}
                </option>
                  {templates && templates.length > 0 ? (
                   templates.map((templateItem, index) => (
                     <option key={index} value={getTemplateId(templateItem)}>
                       {getTemplateTitle(templateItem)}
                     </option>
                   ))
                 ) : (
                  <option value="" disabled>
                    No templates available
                  </option>
                )}
              </select>
             
              {showFileSection && (
                  <div className="mb-3">
                  <label className="form-label">
                    Select {fileType === 'image' ? 'Image' : fileType === 'pdf' ? 'PDF' : 'Video'} 
                    <span className="text-danger">*</span>
                  </label>
                    {previewUrl ? (
                      <div className="file-input-wrapper-hide-text">
                        <input 
                          type="file" 
                          className="form-control hide-file-chosen-text"
                          onChange={handleFileChange}
                          accept={
                            fileType === 'image' ? 'image/*' : 
                            fileType === 'pdf' ? 'application/pdf' : 
                            'video/*'
                          }
                          required={showFileSection && !previewUrl && !fileRemoved}
                        />
                        {currentFileName && (
                          <span className="file-name-display">{currentFileName}</span>
                        )}
                      </div>
                    ) : (
                      <input 
                        type="file" 
                        className="form-control"
                        onChange={handleFileChange}
                        accept={
                          fileType === 'image' ? 'image/*' : 
                          fileType === 'pdf' ? 'application/pdf' : 
                          'video/*'
                        }
                        required={showFileSection && !previewUrl && !fileRemoved}
                      />
                    )}
                  <div className="form-text small">
                    {fileType === 'image' && 'Supported formats: JPG, PNG, GIF. Max size: 5MB'}
                    {fileType === 'pdf' && 'Supported format: PDF. Max size: 5MB'}
                    {fileType === 'video' && 'Supported formats: MP4, AVI, MOV. Max size: 50MB'}
                  </div>
                  </div>
                )}
            </div>

            {/* Right Side - Preview */}
            <div style={{
              flex: '1',
              minWidth: '300px',
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '100%',
                maxWidth: '400px'
              }}>
                <label className="form-label" style={{ marginBottom: '12px', fontWeight: '500', color: '#212121' }}>Preview</label>
                <div className="whatsapp-preview-container" style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '5px 20px 5px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #dee2e6',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <div className="mobile-frame" style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '300px',
                    height: '400px',
                    backgroundImage: `url(${process.env.PUBLIC_URL || ''}/whatsapp_img.png)`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Message bubble positioned on phone background (only when content exists) */}
                    {(description || previewUrl) && (
                      <div style={{
                        position: 'absolute',
                        bottom: '70px',
                        right: '68px',
                        left: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        zIndex: 10
                      }}>
                        <div className="message-bubble" style={{
                          backgroundColor: '#dcf8c6',
                          borderRadius: '7.5px 7.5px 2px 7.5px',
                          padding: '5px',
                          maxWidth: '75%',
                          boxShadow: '0 1px 0.5px rgba(0,0,0,.13)',
                          position: 'relative',
                          wordWrap: 'break-word'
                        }}>
                          {/* Message tail */}
                          <div style={{
                            position: 'absolute',
                            right: '-7px',
                            bottom: '0px',
                            width: '0',
                            height: '0',
                            borderLeft: '7px solid #dcf8c6',
                            borderTop: '7px solid transparent',
                            borderBottom: '7px solid transparent',
                            filter: 'drop-shadow(0 1px 0.5px rgba(0,0,0,.13))'
                          }}></div>
                          {/* Media preview when a file is selected */}
                          {previewUrl && showFileSection && (
                            <div style={{ marginBottom: '1px' }}>
                              {/* Image */}
                              {fileType === 'image' && (
                                <img
                                  src={previewUrl}
                                  alt="Selected"
                          style={{ 
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: 'calc(50px + 100px)',
                                    objectFit: 'contain',
                                    display: 'block',
                                    borderRadius: '10px'
                                  }}
                                />
                              )}
                              {/* Video */}
                              {fileType === 'video' && (
                                <div style={{ position: 'relative' }}>
                                  <video
                                    src={previewUrl}
                                    style={{ width: '100%', maxHeight: 'calc(60px + 100px)', objectFit: 'contain', borderRadius: '10px' }}
                                    muted
                                    playsInline
                                  />
                                  <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '50%',
                                    background: 'rgba(0,0,0,0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <svg width="26" height="26" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z" fill="#fff" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                              {/* PDF */}
                              {fileType === 'pdf' && (
                                <div style={{
                                  background: '#ffffff',
                                  borderRadius: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  padding: '10px 12px',
                                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                                }}>
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '6px',
                                    background: '#fce8e6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#d93025',
                                    flexShrink: 0
                                  }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24">
                                      <path fill="#d93025" d="M13 9V3.5L18.5 9H13z"></path>
                                      <path fill="#d93025" d="M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"></path>
                                    </svg>
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{
                                      fontSize: '14px',
                                      color: '#111b21',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '170px'
                                    }}>{(selectedFile && selectedFile.name) || 'Attachment.pdf'}</div>
                                    <div style={{ fontSize: '12px', color: '#667781' }}>PDF document</div>
                      </div>
                                </div>
                              )}
                    </div>
                  )}
              
                          {/* Caption / description (or plain text when no media) */}
                          {description && (
                            <p style={{
                              margin: '0',
                              fontSize: '14.2px',
                              color: '#303030',
                              lineHeight: '1.5',
                              maxHeight: '90px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              fontFamily: 'Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif',
                              marginBottom: '0px',
                              paddingRight: '2px'
                            }}>
                              {description}
                            </p>
                          )}
                          <div style={{
                            display: 'inline-flex',
                            justifyContent: 'flex-end',
                            alignItems: 'baseline',
                            gap: '4px',
                            marginLeft: '8px',
                            verticalAlign: 'bottom',
                            width: '100%',
                            paddingRight: '2px',
                            paddingLeft: '15px',
                          }}>
                            <span style={{
                              fontSize: '11px',
                              color: '#667781',
                              fontFamily: 'Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif',
                              lineHeight: '15px',
                              whiteSpace: 'nowrap'
                            }}>
                              {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </span>
                            <svg width="16" height="10" viewBox="0 0 16 10" style={{ 
                              marginLeft: '1px',
                              flexShrink: 0,
                              verticalAlign: 'middle'
                            }}>
                              <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" fill="#53bdeb"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>
                <div className="bg-light p-1 d-flex justify-content-end gap-2">
                  <button
                    onClick={handleSave}
                    className="btn btn-info text-white"
                  >
                    SAVE
                  </button>
                </div>
              </div>
            </div>
            
          </div>
         
            </div>
        
        {/* Footer */}
        
      </div>
    </>
  );
};

export default WhatsappDrawer;

